import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { translate } from 'react-i18next';
import { Grid, Row, Col } from 'react-flexbox-grid';
import Spin from 'antd/lib/spin';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import InputNumber from 'antd/lib/input-number';
import DatePicker from 'antd/lib/date-picker';
import moment from 'moment';
import { loanGoals } from '../../../common/enums';
import { getMonthlyPayment, getRpmn, getProvisionFee } from '../../../common/utils/mathHelpers';
import socialStatus, { translation as socialStatusTranslation } from '../../../common/enums/socialStatus';
import { translation as sectorTranslation } from '../../../common/enums/sector';
import {
  currencyFormatter,
  percentageFormatter,
  monthsFormatter,
} from '../../../common/utils/helpers';
import { CalculatorSlider } from '../../../common/components';
import rules from '../../../common/rules/rules';
import { InfoDebtor } from './partials';
import Cookies from 'js-cookie';
import { translation as obligationTypeTranslation } from '../../../common/enums/obligationType';
import { Select, message } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import InsuranceForm from '../../onboarding/components/debtor/insuranceForm';
import IdentityCardCheckForm from '../../onboarding/components/debtor/IdentityCardCheckForm';
import { getInsurancePrice, getLoanCreationAttemptParams, getInterestDiscount } from '../../onboarding/containers/helpers';
import Modal from 'antd/lib/modal';
import { getInterestValue } from '../../onboarding/containers/helpers';
import {
  accountDocumentTypes,
  accountDocumentObligations,
  accountRoles,
  debtorRegistrationActions,
} from '../../../common/enums';

/**
 * Pure recalculation helper shared by recalculateAndSave and handleInsuranceValueChange.
 * Returns the derived state fields for a given (amount, months, insuranceValue) triple,
 * or null when interest cannot be resolved (interest range gap → calculationBlocked).
 */
function computeCalcState(amount, months, insuranceValue, interestRange, settings) {
  let interest = getInterestValue(months, interestRange);
  if (interest === null) return null;

  const discount = getInterestDiscount(insuranceValue, settings);
  if (discount) {
    interest = interest - discount;
  }

  const basePayment = getMonthlyPayment(amount, months, interest);
  const insuranceAmount = getInsurancePrice(insuranceValue, amount, settings);
  const payment = basePayment + insuranceAmount;

  return {
    interest,
    insuranceAmount,
    payment,
    rpmn: getRpmn(amount, basePayment, months),
  };
}

const loanGoalsMap = {
  1: loanGoals.WHATEVER,
  2: loanGoals.VACATION,
  3: loanGoals.CAR,
  4: loanGoals.EXPENSES,
  5: loanGoals.HOUSE
}

const createDocumentUploadType = documentType => `dashboardDocuments/${documentType}`;

@translate()
@Form.create()
export default class CalculatorDebtor extends Component {
  static propTypes = {
    interestRange: PropTypes.array.isRequired,
    calculatorAmountMin: PropTypes.number.isRequired,
    calculatorAmountMax: PropTypes.number.isRequired,
    calculatorAmountStep: PropTypes.number.isRequired,
    calculatorMonthsMin: PropTypes.number.isRequired,
    calculatorMonthsMax: PropTypes.number.isRequired,
    calculatorMonthsStep: PropTypes.number.isRequired,
    defaultAmount: PropTypes.number.isRequired,
    defaultMonths: PropTypes.number.isRequired,
    isInDashboard: PropTypes.bool.isRequired,
    isCreatingLoan: PropTypes.bool.isRequired,
    onSubmit: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    form: PropTypes.object.isRequired,
    loans: PropTypes.array,
    provisionFees: PropTypes.array,
    account: PropTypes.object,
    loanCreationAttempt: PropTypes.object,
    settings: PropTypes.object.isRequired,
    accountDocuments: PropTypes.array.isRequired,
  };

  constructor(props) {
    super(props);

    const { defaultAmount, defaultMonths, interestRange, loanCreationAttempt, account } = props;
    const initialAmount = loanCreationAttempt ? parseFloat(loanCreationAttempt.amount) : defaultAmount;
    const initialMonths = loanCreationAttempt ? parseInt(loanCreationAttempt.termMonths, 10) : defaultMonths;
    const currentInterest = getInterestValue(initialMonths, interestRange);
    const calculationBlocked = currentInterest === null;
    const initialInterest = calculationBlocked ? 0 : currentInterest;
    const initialPayment = calculationBlocked
      ? 0
      : getMonthlyPayment(initialAmount, initialMonths, initialInterest);

    if(!Cookies.get('id')) {
      Cookies.set('id', uuidv4());
    }

    let goal = loanCreationAttempt ? loanGoalsMap[loanCreationAttempt.goal] : loanGoals.WHATEVER
    if (!goal) {
      goal = loanGoals.WHATEVER
    }
    let months = initialMonths
    let payment = loanCreationAttempt ? parseFloat(loanCreationAttempt.payment) : initialPayment

    this.state = {
      goal: goal,
      amount: initialAmount,
      months: months,
      interest: loanCreationAttempt ? parseFloat(loanCreationAttempt.interest) : initialInterest,
      payment: payment,
      paymentTotal: payment * months,
      rpmn: getRpmn(initialAmount, initialPayment, initialMonths),
      calculationBlocked,
      showEmployer: account && account.profileData ? [
        socialStatus.EMPLOYED_INDEFINITE,
        socialStatus.EMPLOYED_DEFINITE,
        socialStatus.AGREEMENT_OTHER,
      ].includes(account.profileData.socialStatus) : false,
      showSector: account && account.profileData && account.profileData.socialStatus ? ![
        socialStatus.UNEMPLOYED,
        socialStatus.STUDENT,
        socialStatus.RETIRED,
        socialStatus.MATERNAL_LEAVE,
      ].includes(account.profileData.socialStatus) : true,
      showCompanyName: account && account.profileData ? [socialStatus.SELF_EMPLOYED, socialStatus.OWN_BUSINESS_INCOME].includes(account.profileData.socialStatus) : false,
      insuranceInfo: loanCreationAttempt ? loanCreationAttempt.insuranceInfo : null,
      insuranceValue: loanCreationAttempt ? loanCreationAttempt.insurance : null,
      insuranceAmount: loanCreationAttempt ? parseFloat(loanCreationAttempt.insuranceAmount) : null,
      showModal: false,
      showInvalidModal: false,
      localStep: 1,
      // step1Data holds the validated step-1 payload until step 2 is submitted
      step1Data: null,
      idCardNumber: account && account.profileData ? account.profileData.idCardNumber : null,
      documentsUploaded: false,
      obligations: [],
      documents: [
        {
            accountRoles: [accountRoles.CREDITOR, accountRoles.DEBTOR],
            allowedDocumentTypes: [accountDocumentTypes.ID_CARD_SCAN_FRONT],
            documentObligation: accountDocumentObligations.REQUIRED,
            documentType: accountDocumentTypes.ID_CARD_SCAN_FRONT,
            documentUploadType: createDocumentUploadType(accountDocumentTypes.ID_CARD_SCAN_FRONT),
            file: {},
            registrationAction: debtorRegistrationActions.UPLOAD_DOCUMENTS,
        },
        {
            accountRoles: [accountRoles.CREDITOR, accountRoles.DEBTOR],
            allowedDocumentTypes: [accountDocumentTypes.ID_CARD_SCAN_BACK],
            documentObligation: accountDocumentObligations.REQUIRED,
            documentType: accountDocumentTypes.ID_CARD_SCAN_BACK,
            documentUploadType: createDocumentUploadType(accountDocumentTypes.ID_CARD_SCAN_BACK),
            file: {},
            registrationAction: debtorRegistrationActions.UPLOAD_DOCUMENTS,
        },
    ],
    };
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.accountDocuments.length !== nextProps.accountDocuments) {
      this.pairDocuments(nextProps.accountDocuments);
    }
  }

  pairDocuments = newDocuments => {
    const filteredDocuments = this.state.documents;
    newDocuments = newDocuments
        .filter(doc => this.props.account ? (new Date(doc.file.updatedAt)).getTime() >= (new Date(this.props.account.updatedAt)).getTime() : true)
        .sort((a, b) => (new Date(b.file.updatedAt)).getTime() - (new Date(a.file.updatedAt)).getTime());

    const newStateDocuments = filteredDocuments.map(stateDocument => {
        const pairedDoc = newDocuments.find(
            doc => stateDocument.allowedDocumentTypes.indexOf(doc.type) !== -1
        );
    
        if (pairedDoc) {
            stateDocument.file = { ...pairedDoc.file };
            stateDocument.documentType = pairedDoc.type;
            stateDocument.documentUploadType = createDocumentUploadType(pairedDoc.type)
        }

        return stateDocument;
    });
    
    this.setState(prevState => ({
        documents: newStateDocuments
    }));
  };

  updateLoanAttemptWithInsurance({cookieId, form, step}) {
    const { months, amount } = this.state
    const { settings, interestRange } = this.props

    try {
      const params = getLoanCreationAttemptParams(form, months, amount, settings, interestRange)
      this.props.updateLoanCreationAttempt({cookieId, form, step, params})
    } catch (e) {
      this.setState({ calculationBlocked: true });
    }
  }

  handleSaveLoanCreationAttempt = e => {
    const { amount, months, payment, interest } = this.state;

    this.props.form.goal = this.state.goal;
    const provisionFee = getProvisionFee(amount, this.props.provisionFees, this.props.loans);
    this.props.form.provisionFee = provisionFee;
    this.props.form.amount = amount
    this.props.form.months = months
    this.props.form.payment = payment
    this.props.form.interest = interest

    this.updateLoanAttemptWithInsurance({ cookieId: Cookies.get('id'), form: this.props.form, step: 0 });
  }

  componentDidMount() {
    if(typeof (this.props.location.state) !== 'undefined'){

      if (this.props.location.state.fromHelp === true){
        setTimeout(() => {
          this.executeScroll()
        }, 100);
      }
    }
  }


  executeScroll = () => {
    let scrolling = this.highlight
    scrolling.scrollIntoView({behavior: 'smooth'})
  }

  recalculateAndSave = () => {
    const { interestRange, settings } = this.props;
    const { amount, months, insuranceValue } = this.state;

    const calc = computeCalcState(amount, months, insuranceValue, interestRange, settings);
    if (!calc) {
      this.setState({ calculationBlocked: true });
      return;
    }

    this.setState({
      ...calc,
      calculationBlocked: false,
    }, () => {
      this.handleSaveLoanCreationAttempt();
    });
  };

  onGoalChange = goal => {
    this.setState({ goal }, () => {
      this.handleSaveLoanCreationAttempt();
    });
  };

  onSliderValueChange = (val, field, min, max) => {
    let amount = val;

    if (val < min || val > max) {
      amount = val < min ? min : max;
    }

    this.props.form.setFieldsValue({ [field]: amount });
    this.setState(
      {
        [field]: amount,
      },
      this.recalculateAndSave
    );
  };

  onAmountChange = val => {
    const { calculatorAmountMin, calculatorAmountMax } = this.props;
    this.onSliderValueChange(val, 'amount', calculatorAmountMin, calculatorAmountMax);
  };

  onMonthsChange = val => {
    const { calculatorMonthsMin, calculatorMonthsMax } = this.props;
    this.onSliderValueChange(val, 'months', calculatorMonthsMin, calculatorMonthsMax);
  };

  onSliderInputBlur = field => {
    this.props.form.validateFields([field], { force: true }, (errors, values) => {
      if (!errors) {
        this.setState(
          {
            [field]: values[field],
          },
          this.recalculateAndSave
        );
      }
    });
  };

  checkInsurance() {
    const form = this.props.form

    const insurance = this.state.insuranceValue

    if (insurance && insurance !== 'C') {
      if (
        !form.getFieldValue('youngerThanLimit') ||
        form.getFieldValue('pension') ||
        form.getFieldValue('invalidity') ||
        form.getFieldValue('unableToWork')
      ) {
        this.setState({showModal: true})
        return false
      }
    }

    return true
  }

  // ─── Step-1 submit (dashboard repeat-borrower flow) ───────────────────────
  // Validates amount/months + scoring fields and advances to the insurance step.
  handleStepOneSubmit = () => {
    const { goal, amount, months, interest, payment, rpmn, calculationBlocked } = this.state;
    const { form, t, isInDashboard } = this.props;

    if (calculationBlocked) {
      message.error(t('Nie je možné určiť úrok pôžičky'));
      return;
    }

    if (isInDashboard) {
      const invalidObligations = this.state.obligations.filter(o => {
        if (!o.type) return false;
        if (!o.originalAmount || o.originalAmount <= 0) return true;
        if (!o.provider || o.provider.trim() === '') return true;
        if (o.type !== 'KREDITNA_KARTA' && o.type !== 'KONTOKORENT') {
          if (!o.monthlyPayment || o.monthlyPayment <= 0) return true;
        }
        return false;
      });

      if (invalidObligations.length > 0) {
        message.error(t('Vyplňte všetky povinné polia záväzkov'));
        return;
      }
    }

    const provisionFee = getProvisionFee(amount, this.props.provisionFees, this.props.loans);
    form.provisionFee = provisionFee;
    form.goal = goal;
    form.amount = amount;
    form.months = months;
    form.payment = payment;
    form.interest = interest;
    form.utm_source = localStorage.getItem('utm_source');
    form.utm_medium = localStorage.getItem('utm_medium');
    form.utm_campaign = localStorage.getItem('utm_campaign');
    this.handleSaveLoanCreationAttempt();

    const validObligations = this.state.obligations
      .filter(o => o.type)
      .map(o => {
        const cleaned = { ...o };
        if (cleaned.type === 'KREDITNA_KARTA' || cleaned.type === 'KONTOKORENT') {
          delete cleaned.monthlyPayment;
        }
        return cleaned;
      });

    const scoringFields = [
      'yearsAtAddress', 'socialStatus', 'incomeFrom',
      ...(this.state.showSector ? ['sector'] : []),
      ...((this.state.showEmployer || this.state.showCompanyName) ? ['employer'] : []),
    ];

    form.validateFields(['amount', 'months', ...scoringFields], { force: true }, (errors, values) => {
      if (!errors) {
        const socialStatusVal = form.getFieldValue('socialStatus');
        const employerVal = form.getFieldValue('employer');
        const sectorVal = form.getFieldValue('sector');
        const incomeDateVal = form.getFieldValue('incomeFrom');
        const taxDebtVal = form.getFieldValue('insuranceTaxDebt');

        this.setState({
          localStep: 2,
          step1Data: {
            goal,
            amount,
            months,
            interest,
            payment,
            paymentTotal: payment * months,
            rpmn,
            provisionFee,
            utm_source: form.utm_source,
            utm_medium: form.utm_medium,
            utm_campaign: form.utm_campaign,
            idCardNumber: form.getFieldValue('idCardNumber'),
            obligations: validObligations,
            insuranceTaxDebt: taxDebtVal !== undefined && taxDebtVal !== null ? taxDebtVal : null,
            socialStatus: socialStatusVal !== undefined && socialStatusVal !== null ? socialStatusVal : null,
            employer: employerVal !== undefined && employerVal !== null ? employerVal : null,
            sector: this.state.showSector && sectorVal !== undefined && sectorVal !== null ? sectorVal : null,
            yearsAtAddress: form.getFieldValue('yearsAtAddress'),
            incomeFrom: incomeDateVal ? incomeDateVal.format('YYYY-MM-DD') : null,
          },
        });
      }
    });
  };

  // ─── Step-2 submit (dashboard repeat-borrower flow) ───────────────────────
  // Gathers insurance form data, builds the full insuranceInfo object, and
  // calls this.props.onSubmit with the complete payload identical to today's.
  handleStepTwoSubmit = () => {
    const { form } = this.props;
    const { insuranceValue, insuranceAmount, step1Data } = this.state;

    // Validate only the insurance-related fields rendered in this step.
    const insuranceFields = [
      'youngerThanLimit', 'pension', 'invalidity', 'unableToWork',
      'inRelationshipWithInsCompany', 'isPoliticalPerson', 'isSanctioned',
      'isFinalBenefitor', 'finalBenefitor', 'insuranceNotes',
    ];

    form.validateFields(insuranceFields, { force: true }, (err, formData) => {
      if (err) return;

      if (!this.checkInsuranceEligibility()) return;

      const insuranceInfo = insuranceValue
        ? {
            packageType: insuranceValue,
            insuranceAmount: insuranceAmount,
            interestDiscount: 0, // backend derives the real discount from INSURANCE_X_INTEREST_DISCOUNT system-setting; this field is intentional parity with onboarding (OnboardingDebtorContainer.js)
            ageRestriction: insuranceValue === 'C' ? 70 : 62,
            youngerThanLimit: formData.youngerThanLimit,
            pension: formData.pension,
            invalidity: formData.invalidity,
            unableToWork: formData.unableToWork,
            notes: formData.insuranceNotes,
            inRelationshipWithInsCompany: formData.inRelationshipWithInsCompany,
            isPoliticalPerson: formData.isPoliticalPerson,
            isSanctioned: formData.isSanctioned,
            isFinalBenefitor: formData.isFinalBenefitor,
            finalBenefitor: formData.finalBenefitor,
          }
        : null;

      this.props.onSubmit({
        ...step1Data,
        insurance: insuranceValue,
        insuranceAmount: insuranceAmount,
        insuranceInfo: insuranceInfo,
      });
    });
  };

  // Eligibility check for the insurance step (mirrors StepInsurance pattern).
  checkInsuranceEligibility() {
    const { form } = this.props;
    const { insuranceValue } = this.state;

    if (insuranceValue && insuranceValue !== 'C') {
      if (
        !form.getFieldValue('youngerThanLimit') ||
        form.getFieldValue('pension') ||
        form.getFieldValue('invalidity') ||
        form.getFieldValue('unableToWork')
      ) {
        this.setState({ showInvalidModal: true });
        return false;
      }
    }
    return true;
  }

  handleStepBack = () => {
    this.setState({ localStep: 1, showInvalidModal: false });
  };

  // "Pokračovať bez poistenia" from the ineligibility modal.
  continueWithoutInsurance = () => {
    const { form } = this.props;
    const insuranceFields = ['A', 'B', 'C'];
    insuranceFields.forEach(key => {
      form.setFieldsValue({ [`insurance${key}`]: false });
    });

    this.setState(
      { insuranceValue: null, insuranceAmount: null, showInvalidModal: false },
      () => {
        this.recalculateAndSave();
        // Build payload directly without insurance (no eligibility check needed).
        const { step1Data } = this.state;
        this.props.onSubmit({
          ...step1Data,
          insurance: null,
          insuranceAmount: null,
          insuranceInfo: null,
        });
      }
    );
  };

  handleInsuranceValueChange = (value) => {
    const { insuranceValue } = this.state;
    const { interestRange, settings, form } = this.props;

    const newVal = insuranceValue === value ? null : value;

    const insuranceFields = ['A', 'B', 'C'];
    for (const key of insuranceFields) {
      form.setFieldsValue({ [`insurance${key}`]: newVal === key });
    }

    const { amount, months } = this.state;
    const calc = computeCalcState(amount, months, newVal, interestRange, settings);

    this.setState(
      {
        insuranceValue: newVal,
        ...(calc ? { ...calc, calculationBlocked: false } : { calculationBlocked: true }),
      },
      () => { this.handleSaveLoanCreationAttempt(); }
    );
  }

  handleSocialStatusChange = (value) => {
    const showEmployerStatuses = [
      socialStatus.EMPLOYED_INDEFINITE,
      socialStatus.EMPLOYED_DEFINITE,
      socialStatus.AGREEMENT_OTHER,
    ];
    const noSectorStatuses = [
      socialStatus.UNEMPLOYED,
      socialStatus.STUDENT,
      socialStatus.RETIRED,
      socialStatus.MATERNAL_LEAVE,
    ];
    const shouldHideSector = noSectorStatuses.includes(value);
    const shouldHideEmployer = !showEmployerStatuses.includes(value) && value !== socialStatus.SELF_EMPLOYED;
    this.setState({
      showEmployer: showEmployerStatuses.includes(value),
      showSector: !shouldHideSector,
      showCompanyName: [socialStatus.SELF_EMPLOYED, socialStatus.OWN_BUSINESS_INCOME].includes(value),
    });
    if (shouldHideSector) {
      this.props.form.setFieldsValue({ sector: undefined });
    }
    if (shouldHideEmployer) {
      this.props.form.setFieldsValue({ employer: undefined });
    }
  };

  onIdCardChange(idCardNumber) {
    this.setState({idCardNumber});
  }

  addObligation = () => {
    this.setState(prev => ({
      obligations: [...prev.obligations, { type: null, originalAmount: null, monthlyPayment: null, provider: '' }],
    }));
  };

  removeObligation = (index) => {
    this.setState(prev => ({
      obligations: prev.obligations.filter((_, i) => i !== index),
    }));
  };

  updateObligation = (index, field, value) => {
    this.setState(prev => {
      const obligations = [...prev.obligations];
      obligations[index] = { ...obligations[index], [field]: value };
      return { obligations };
    });
  };

  handleSubmit = () => {
    const { goal, amount, months, interest, payment, rpmn, insuranceValue, insuranceAmount, insuranceInfo, calculationBlocked } = this.state;
    const { form, t, isInDashboard } = this.props;

    if (calculationBlocked) {
      message.error(t('Nie je možné určiť úrok pôžičky'));
      return;
    }

    // Validate obligations when in dashboard (repeat borrower)
    if (isInDashboard) {
      const invalidObligations = this.state.obligations.filter(o => {
        if (!o.type) return false;
        if (!o.originalAmount || o.originalAmount <= 0) return true;
        if (!o.provider || o.provider.trim() === '') return true;
        if (o.type !== 'KREDITNA_KARTA' && o.type !== 'KONTOKORENT') {
          if (!o.monthlyPayment || o.monthlyPayment <= 0) return true;
        }
        return false;
      });

      if (invalidObligations.length > 0) {
        message.error(t('Vyplňte všetky povinné polia záväzkov'));
        return;
      }
    }

    const provisionFee = getProvisionFee(amount, this.props.provisionFees, this.props.loans);
    form.provisionFee = provisionFee;
    form.goal = goal;
    form.nextPage = "yes";
    form.amount = amount
    form.months = months
    form.payment = payment
    form.interest = interest
    form.utm_source = localStorage.getItem("utm_source")
    form.utm_medium = localStorage.getItem("utm_medium")
    form.utm_campaign = localStorage.getItem("utm_campaign")
    this.handleSaveLoanCreationAttempt();

    // Filter and clean obligations for submission
    const validObligations = this.state.obligations
      .filter(o => o.type)
      .map(o => {
        const cleaned = { ...o };
        if (cleaned.type === 'KREDITNA_KARTA' || cleaned.type === 'KONTOKORENT') {
          delete cleaned.monthlyPayment;
        }
        return cleaned;
      });

    const scoringFields = isInDashboard
      ? ['yearsAtAddress', 'socialStatus', 'incomeFrom',
         ...(this.state.showSector ? ['sector'] : []),
         ...((this.state.showEmployer || this.state.showCompanyName) ? ['employer'] : [])]
      : [];

    form.validateFields(['amount', 'months', ...scoringFields], { force: true }, (errors, values) => {
      if (!errors && this.checkInsurance()) {
        const submitData = {
          goal,
          amount,
          months,
          interest,
          payment,
          paymentTotal: payment * months,
          rpmn,
          provisionFee,
          insurance: insuranceValue,
          insuranceAmount: insuranceAmount,
          insuranceInfo: insuranceInfo,
          utm_source: form.utm_source,
          utm_medium: form.utm_medium,
          utm_campaign: form.utm_campaign,
          idCardNumber: form.getFieldValue('idCardNumber'),
        };

        if (isInDashboard) {
          submitData.obligations = validObligations;
          const taxDebtVal = form.getFieldValue('insuranceTaxDebt');
          submitData.insuranceTaxDebt = taxDebtVal !== undefined && taxDebtVal !== null ? taxDebtVal : null;
          // Scoring fields
          const socialStatusVal = form.getFieldValue('socialStatus');
          submitData.socialStatus = (socialStatusVal !== undefined && socialStatusVal !== null) ? socialStatusVal : null;
          const employerVal = form.getFieldValue('employer');
          submitData.employer = (employerVal !== undefined && employerVal !== null) ? employerVal : null;
          const sectorVal = form.getFieldValue('sector');
          submitData.sector = this.state.showSector && sectorVal !== undefined && sectorVal !== null ? sectorVal : null;
          submitData.yearsAtAddress = form.getFieldValue('yearsAtAddress');
          const incomeDateVal = form.getFieldValue('incomeFrom');
          submitData.incomeFrom = incomeDateVal ? incomeDateVal.format('YYYY-MM-DD') : null;
        }

        this.props.onSubmit(submitData);
      }
    });
  };

  renderContent() {
    const {
      calculatorAmountMin,
      calculatorAmountMax,
      calculatorAmountStep,
      calculatorMonthsMin,
      calculatorMonthsMax,
      calculatorMonthsStep,
      isCreatingLoan,
      isInDashboard,
      t,
      form,
      loans,
      loanCreationAttempt,
      accountDocuments,
      account,
    } = this.props;
    const { amount, months, interest, payment, rpmn, insuranceAmount, insuranceValue, documents, calculationBlocked } = this.state;
    const provisionFee = getProvisionFee(amount, this.props.provisionFees, loans);

    if (calculationBlocked) {
      return (
        <section className="calculator">
          <p>{t('Nie je možné načítať nastavenia kalkulačky.')}</p>
        </section>
      );
    }

    const submitDisabled = form.getFieldValue('hasSameID') === false && (documents.some(d => !d.file.id) || !form.getFieldValue('idCardNumber'));

    return (
      <Spin spinning={isCreatingLoan}>
        <section className="calculator">
          <AmountMonthsControllsT
            onChangeAmount={this.onAmountChange}
            onBlurAmountInput={() => this.onSliderInputBlur('amount')}
            onBlurMonthsInput={() => this.onSliderInputBlur('months')}
            amount={amount}
            amountMin={calculatorAmountMin}
            amountMax={calculatorAmountMax}
            amountStep={calculatorAmountStep}
            onChangeMonths={this.onMonthsChange}
            months={months}
            monthsMin={calculatorMonthsMin}
            monthsMax={calculatorMonthsMax}
            monthsStep={calculatorMonthsStep}
            form={form}
            defaultAmount={amount}
            defaultMonths={months}
          />
          <ValuesT
            interest={interest}
            payment={payment}
            rpmn={rpmn}
            provisionFee={provisionFee}
            insuranceAmount={insuranceAmount}
            insuranceSelected={!!insuranceValue}
          />
          <IdentityCardCheckForm
            form={form}
            profileData={account ? account.profileData : null}
            documents={documents}
            allDocuments={accountDocuments}
          />
          {!isInDashboard && (
            <InsuranceForm
              form={form}
              t={t}
              payment={payment}
              insuranceAmount={insuranceAmount}
              insuranceInfo={loanCreationAttempt ? loanCreationAttempt.insuranceInfo : null}
              onBlurSaveLoanData={() => {
                setTimeout(() => this.handleSaveLoanCreationAttempt(), 10)
              }}
              showEmployer={this.state.showEmployer}
              handleInsuranceValueChange={this.handleInsuranceValueChange}
              insuranceValue={this.state.insuranceValue}
            />
          )}
          {isInDashboard && (
            <div style={{ marginTop: '24px' }}>
              <div className="section-title">{t('Osobné údaje pre scoring')}</div>
              <Row>
                <Col xs={12} sm={6}>
                  <Form.Item label={t('Počet rokov bývania na adrese')}>
                    {form.getFieldDecorator('yearsAtAddress', {
                      initialValue: account && account.profileData && account.profileData.address
                        ? account.profileData.address.yearsAtAddress : undefined,
                      rules: [{ required: true, message: t('Povinné pole') }],
                    })(
                      <InputNumber min={0} max={99} precision={0} style={{ width: '100%' }} />
                    )}
                  </Form.Item>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Item label={t('Zdroj príjmu')}>
                    {form.getFieldDecorator('socialStatus', {
                      initialValue: account && account.profileData ? account.profileData.socialStatus : undefined,
                      rules: [{ required: true, message: t('Povinné pole') }],
                    })(
                      <Select onChange={this.handleSocialStatusChange} placeholder={t('Vyberte zdroj príjmu')}>
                        {Object.entries(socialStatusTranslation).map(([key, label]) => (
                          <Select.Option key={key} value={key}>{label}</Select.Option>
                        ))}
                      </Select>
                    )}
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col xs={12} sm={6}>
                  <Form.Item label={t('Príjem od')}>
                    {form.getFieldDecorator('incomeFrom', {
                      initialValue: account && account.profileData && account.profileData.incomeFrom
                        ? moment(account.profileData.incomeFrom) : undefined,
                      rules: [{ required: true, message: t('Povinné pole') }],
                    })(
                      <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
                    )}
                  </Form.Item>
                </Col>
                {this.state.showSector && (
                  <Col xs={12} sm={6}>
                    <Form.Item label={t('Odvetvie')}>
                      {form.getFieldDecorator('sector', {
                        initialValue: account && account.profileData ? account.profileData.sector : undefined,
                        rules: [{ required: true, message: t('Povinné pole') }],
                      })(
                        <Select placeholder={t('Vyberte odvetvie')}>
                          {Object.entries(sectorTranslation).map(([key, label]) => (
                            <Select.Option key={key} value={Number(key)}>{label}</Select.Option>
                          ))}
                        </Select>
                      )}
                    </Form.Item>
                  </Col>
                )}
              </Row>
              {(this.state.showEmployer || this.state.showCompanyName) && (
                <Row>
                  <Col xs={12} sm={6}>
                    <Form.Item label={t(this.state.showCompanyName ? 'Názov spoločnosti' : 'Názov zamestnávateľa')}>
                      {form.getFieldDecorator('employer', {
                        initialValue: account && account.profileData ? account.profileData.employer : undefined,
                        rules: [{ required: true, message: t('Povinné pole') }],
                      })(
                        <Input placeholder={t(this.state.showCompanyName ? 'Názov spoločnosti' : 'Názov zamestnávateľa')} />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              )}
              <div className="section-title">{t('Záväzky')}</div>
              <p style={{ fontSize: '16px', marginBottom: '16px' }}>
                {t('Doplňte údaje o Vašich úveroch, kreditných kartách, prípadne iných záväzkoch.')}
              </p>

              {this.state.obligations.map((obligation, index) => (
                <Row key={index} style={{ marginBottom: '8px', alignItems: 'flex-start' }}>
                  <Col xs={12} sm={3}>
                    <Form.Item label={index === 0 ? t('Typ záväzku') : ''}>
                      <Select
                        value={obligation.type}
                        onChange={(val) => this.updateObligation(index, 'type', val)}
                        placeholder={t('Vyberte typ')}
                      >
                        {Object.entries(obligationTypeTranslation).map(([key, label]) => (
                          <Select.Option key={key} value={key}>{label}</Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={3}>
                    <Form.Item label={index === 0 ? t('Pôvodná výška v EUR') : ''}>
                      <InputNumber
                        min={0}
                        precision={0}
                        style={{ width: '100%' }}
                        value={obligation.originalAmount}
                        onChange={(val) => this.updateObligation(index, 'originalAmount', val)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={3}>
                    <Form.Item label={index === 0 ? t('Mesačná splátka v EUR') : ''}>
                      <InputNumber
                        min={0}
                        precision={0}
                        style={{ width: '100%' }}
                        value={obligation.type === 'KREDITNA_KARTA' || obligation.type === 'KONTOKORENT' ? undefined : obligation.monthlyPayment}
                        onChange={(val) => this.updateObligation(index, 'monthlyPayment', val)}
                        disabled={obligation.type === 'KREDITNA_KARTA' || obligation.type === 'KONTOKORENT'}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={2}>
                    <Form.Item label={index === 0 ? t('Kto poskytol') : ''}>
                      <Input
                        maxLength={20}
                        value={obligation.provider}
                        onChange={(e) => this.updateObligation(index, 'provider', e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={1} style={index === 0 ? { paddingTop: '38px' } : {}}>
                    <Button type="link" icon="close" onClick={() => this.removeObligation(index)} style={{ fontSize: '14px', width: '32px', height: '58px' }} />
                  </Col>
                </Row>
              ))}

              <Button type="dashed" onClick={this.addObligation} style={{ width: '100%', marginBottom: '16px' }}>
                + {t('Pridať záväzok')}
              </Button>

              <Row>
                <Col xs={12} sm={6}>
                  <Form.Item label={t('Dlh na poistnom alebo daniach v EUR')}>
                    {form.getFieldDecorator('insuranceTaxDebt', {
                      initialValue: account && account.profileData ? account.profileData.insuranceTaxDebt : undefined,
                      rules: [],
                    })(
                      <InputNumber
                        min={0}
                        precision={0}
                        style={{ width: '100%' }}
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </div>
          )}
          <Row>
            <Col xs={12} sm={4} smOffset={2}>
              <Button
                onClick={isInDashboard ? this.handleStepOneSubmit : this.handleSubmit}
                type="primary"
                size="large"
                className="btn-block"
                disabled={submitDisabled}
              >
                {isInDashboard ? t('Ďalej') : t('Požičať si')}
              </Button>
            </Col>
          </Row>
          {!isInDashboard && (
            <Modal
              title={t('Nie je možné vytvoriť poistenie')}
              visible={this.state.showModal}
              onCancel={() => this.setState({showModal: false})}
              width={600}
              footer={
                <Row>
                  <Col sm={6}>
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => {
                        this.setState({showModal: false})
                        this.handleInsuranceValueChange(this.state.insuranceValue)
                      }}
                    >
                      {t('Pokračovať bez poistenia')}
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      size="small"
                      type="primary"
                      onClick={(e) => {
                        this.setState({showModal: false})
                        this.handleInsuranceValueChange('C')
                      }}
                    >
                      {t('Pokračovať s poistením C')}
                    </Button>
                  </Col>
                </Row>
              }
            >
              <div>
                {t('Žiaľ, na základe vami zvolených údajov vám nevieme ponúknuť vybraný poistný balík ')}
                {this.state.insuranceValue}
                {t(', avšak vieme vám ponúknť poistný balík C.')}
              </div>
            </Modal>
          )}
        </section>
      </Spin>
    );
  }

  // ─── Step 2: Insurance selection (repeat-borrower dashboard flow) ────────
  renderInsuranceStep() {
    const { form, t, isCreatingLoan, loanCreationAttempt } = this.props;
    const { payment, insuranceAmount, insuranceValue, showInvalidModal } = this.state;

    return (
      <Spin spinning={isCreatingLoan}>
        <section className="onboarding-form">
          <Form layout="vertical">
            <InsuranceForm
              form={form}
              t={t}
              payment={payment}
              insuranceAmount={insuranceAmount || 0}
              insuranceInfo={loanCreationAttempt ? loanCreationAttempt.insuranceInfo : null}
              onBlurSaveLoanData={() => {
                setTimeout(() => this.handleSaveLoanCreationAttempt(), 10);
              }}
              showEmployer={this.state.showEmployer}
              handleInsuranceValueChange={this.handleInsuranceValueChange}
              insuranceValue={insuranceValue}
            />

            <Row center="xs" className="form-actions-row">
              <Col xs={12} sm={6} className="form-actions-col">
                <a onClick={this.handleStepBack}>{t('Späť')}</a>
                <Button
                  type="primary"
                  size="large"
                  className="btn-block"
                  onClick={this.handleStepTwoSubmit}
                  loading={isCreatingLoan}
                >
                  {t('Dokončiť žiadosť')}
                </Button>
              </Col>
            </Row>
          </Form>
        </section>

        <Modal
          title={t('Nie je možné vytvoriť poistenie')}
          visible={showInvalidModal}
          onCancel={() => this.setState({ showInvalidModal: false })}
          width={600}
          footer={
            <Row>
              <Col sm={6}>
                <Button
                  size="small"
                  type="primary"
                  onClick={this.continueWithoutInsurance}
                >
                  {t('Pokračovať bez poistenia')}
                </Button>
              </Col>
              <Col>
                <Button
                  size="small"
                  type="primary"
                  onClick={() => {
                    this.handleInsuranceValueChange('C');
                    this.setState({ showInvalidModal: false });
                  }}
                >
                  {t('Pokračovať s poistením C')}
                </Button>
              </Col>
            </Row>
          }
        >
          <div>
            {t('Žiaľ, na základe vami zvolených údajov vám nevieme ponúknuť vybraný poistný balík ')}
            {insuranceValue}
            {t(', avšak vieme vám ponúknť poistný balík C.')}
          </div>
        </Modal>
      </Spin>
    );
  }

  renderForOnboarding() {
    return (
      <div className="calculator-wrapper">
        <div className="image-placeholder">
          <Grid>
            <InfoListT />
            {this.renderContent()}
          </Grid>
        </div>
        <div ref={ (info) => {this.highlight = info}}>
          <InfoDebtor/>
        </div>
      </div>
    );
  }

  render() {
    if (this.props.isInDashboard) {
      if (this.state.localStep === 2) {
        return <div className="calculator-wrapper">{this.renderInsuranceStep()}</div>;
      }
      return <div className="calculator-wrapper">{this.renderContent()}</div>;
    }

    return this.renderForOnboarding();
  }
}

const InfoList = ({ t }) => (
  <Row className="infolist">
    <Col xs={12}>
      <h3>{t('Prečo si požičať cez nás')}</h3>
      <ul>
        <li>{t('Pôžičku si vyberiem sám')}</li>
        <li>{t('Výhodnejšie ako v banke alebo nebankovke')}</li>
        <li>{t('Bez skrytých podmienok a poplatkov')}</li>
        <li>{t('Môžem si požičať priamo z domu')}</li>
        <li>{t('Moderná, bezpečná a vo svete populárna forma pôžičky')}</li>
      </ul>
    </Col>
  </Row>
);

InfoList.propTypes = {
  t: PropTypes.func.isRequired,
};

const InfoListT = translate()(InfoList);

const AmountMonthsControlls = ({
  t,
  onChangeAmount,
  onChangeMonths,
  onBlurAmountInput,
  onBlurMonthsInput,
  amount,
  amountMin,
  amountMax,
  amountStep,
  months,
  monthsMin,
  monthsMax,
  monthsStep,
  form,
  defaultAmount,
  defaultMonths,
}) => (
  <Row between="xs" className="amount-wrapper">
    <Col xs={12} md={6} lg={7}>
      <div className="input-number">
        <p>{t('chcem si požičať (€)')}</p>
        <Form.Item>
          {form.getFieldDecorator('amount', {
            rules: [
              ...rules.required,
              rules.number,
              rules.positiveNumber,
              rules.divisibleBy(amountStep),
            ],
            initialValue: defaultAmount,
          })(
            <InputNumber
              size="default"
              onBlur={onBlurAmountInput}
              min={amountMin}
              max={amountMax}
              step={amountStep}
            />
          )}
        </Form.Item>
      </div>
      <CalculatorSlider
        formatter={currencyFormatter}
        onChange={onChangeAmount}
        value={amount}
        min={amountMin}
        max={amountMax}
        step={amountStep}
      />
    </Col>
    <Col xs={12} md={6} lg={5} className="months-picker-wrapper">
      <div className="input-number">
        <p>{t('Na dobu splácania')}</p>
        <Form.Item>
          {form.getFieldDecorator('months', {
            rules: [...rules.required, rules.number, rules.positiveNumber],
            initialValue: defaultMonths,
          })(
            <InputNumber
              size="default"
              onBlur={onBlurMonthsInput}
              min={monthsMin}
              max={monthsMax}
              step={monthsStep}
            />
          )}
          <p>{monthsFormatter(form.getFieldValue('months'))}</p>
        </Form.Item>
      </div>
      <CalculatorSlider
        formatter={val => monthsFormatter(val, true)}
        onChange={onChangeMonths}
        value={months}
        min={monthsMin}
        max={monthsMax}
        step={monthsStep}
      />
    </Col>
  </Row>
);

AmountMonthsControlls.propTypes = {
  t: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  onBlurAmountInput: PropTypes.func.isRequired,
  onBlurMonthsInput: PropTypes.func.isRequired,
  onChangeAmount: PropTypes.func.isRequired,
  onChangeMonths: PropTypes.func.isRequired,
  amount: PropTypes.number.isRequired,
  amountMin: PropTypes.number.isRequired,
  amountMax: PropTypes.number.isRequired,
  amountStep: PropTypes.number.isRequired,
  months: PropTypes.number.isRequired,
  monthsMin: PropTypes.number.isRequired,
  monthsMax: PropTypes.number.isRequired,
  monthsStep: PropTypes.number.isRequired,
  defaultAmount: PropTypes.number.isRequired,
  defaultMonths: PropTypes.number.isRequired,
};

const AmountMonthsControllsT = translate()(AmountMonthsControlls);

const Values = ({ t, interest, payment, rpmn, provisionFee, insuranceAmount, insuranceSelected }) => {
  const hasInsurance = insuranceSelected || insuranceAmount > 0;
  const monthlyInsuranceAmount = insuranceAmount || 0;

  return (
  <Row center="xs" middle="xs" between="xs" className="summary">
    <Col sm={12}>
      <Row center="xs" middle="xs" around="xs">
        {/* <Col xs={2}>
          <div>
            <div>
              <strong>{percentageFormatter(interest)}</strong>
            </div>
            <div>{t('úrok')}</div>
          </div>
        </Col> */}
        <Col xs={2}>
          <div>
            <div>
              <strong>{percentageFormatter(rpmn)}</strong>
            </div>
            <div>{t('RPMN')}</div>
          </div>
        </Col>
        {
          hasInsurance ?
          <Col xs={2}>
            <div>
              <div>
                <strong>{currencyFormatter(payment - monthlyInsuranceAmount)}</strong>
              </div>
              <div>{t('mesačná splátka')}</div>
            </div>
          </Col> : null
        }
        {
          hasInsurance ?
          <Col xs={2}>
            <div>
              <div>
                <strong>{currencyFormatter(monthlyInsuranceAmount)}</strong>
              </div>
              <div>{t('poistenie')}</div>
            </div>
          </Col> : null
        }
        <Col xs={3}>
          <div>
            <div>
              <strong>{currencyFormatter(payment)}</strong>
            </div>
            <div>{hasInsurance ? t('splátka + poistenie') : t('mesačná splátka')}</div>
          </div>
        </Col>
        <Col xs={3}>
          <div>
            <div><strong>{currencyFormatter(provisionFee)}</strong></div>
            <div>{t('Poplatok za poskytnutie požičky')}</div>
          </div>
        </Col>
      </Row>

    </Col>
  </Row>
  );
};

Values.propTypes = {
  t: PropTypes.func.isRequired,
  interest: PropTypes.number.isRequired,
  payment: PropTypes.number.isRequired,
  rpmn: PropTypes.number.isRequired,
  provisionFee: PropTypes.number.isRequired,
  insuranceAmount: PropTypes.number,
  insuranceSelected: PropTypes.bool,
};

const ValuesT = translate()(Values);



// WEBPACK FOOTER //
// ./src/features/calculator/components/CalculatorDebtor.js